import React, {FC} from 'react';

import modelAiWrite1 from 'assets/v3/guide/model/ai-write-1.webp';
import modelAiWrite2 from 'assets/v3/guide/model/ai-write-2.webp';
import modelEntry from 'assets/v3/guide/model/entry.webp';
import modelRegen from 'assets/v3/guide/model/regen.webp';
import modelShowClothes1 from 'assets/v3/guide/model/show-clothes-1.webp';
import modelShowClothes2 from 'assets/v3/guide/model/show-clothes-2.webp';
import modelShowCup1 from 'assets/v3/guide/model/show-cup-1.webp';
import modelShowCup2 from 'assets/v3/guide/model/show-cup-2.webp';
import modelShowEarring1 from 'assets/v3/guide/model/show-earring-1.webp';
import modelShowEarring2 from 'assets/v3/guide/model/show-earring-2.webp';
import modelShowLamp1 from 'assets/v3/guide/model/show-lamp-1.webp';
import modelShowLamp2 from 'assets/v3/guide/model/show-lamp-2.webp';
import modelShowNecklace1 from 'assets/v3/guide/model/show-necklace-1.webp';
import modelShowNecklace2 from 'assets/v3/guide/model/show-necklace-2.webp';
import modelShowPruner1 from 'assets/v3/guide/model/show-pruner-1.webp';
import modelShowPruner2 from 'assets/v3/guide/model/show-pruner-2.webp';
import modelUploadFront from 'assets/v3/guide/model/upload-front.webp';
import modelUploadScene from 'assets/v3/guide/model/upload-scene.webp';
import sceneAiWrite1 from 'assets/v3/guide/scene/ai-write-1.webp';
import sceneAiWrite2 from 'assets/v3/guide/scene/ai-write-2.webp';
import sceneEntry from 'assets/v3/guide/scene/entry.webp';
import sceneRegen from 'assets/v3/guide/scene/regen.webp';
import sceneShowEarphone1 from 'assets/v3/guide/scene/show-earphone-1.webp';
import sceneShowEarphone2 from 'assets/v3/guide/scene/show-earphone-2.webp';
import sceneShowFeeder1 from 'assets/v3/guide/scene/show-feeder-1.webp';
import sceneShowFeeder2 from 'assets/v3/guide/scene/show-feeder-2.webp';
import sceneShowNecklace1 from 'assets/v3/guide/scene/show-necklace-1.webp';
import sceneShowNecklace2 from 'assets/v3/guide/scene/show-necklace-2.webp';
import sceneShowOil1 from 'assets/v3/guide/scene/show-oil-1.webp';
import sceneShowOil2 from 'assets/v3/guide/scene/show-oil-2.webp';
import sceneShowPerfume1 from 'assets/v3/guide/scene/show-perfume-1.webp';
import sceneShowPerfume2 from 'assets/v3/guide/scene/show-perfume-2.webp';
import sceneShowPruner1 from 'assets/v3/guide/scene/show-pruner-1.webp';
import sceneShowPruner2 from 'assets/v3/guide/scene/show-pruner-2.webp';
import sceneShowUmbrella1 from 'assets/v3/guide/scene/show-umbrella-1.webp';
import sceneShowUmbrella2 from 'assets/v3/guide/scene/show-umbrella-2.webp';
import sceneUploadSample from 'assets/v3/guide/scene/upload-sample.webp';

/** 使用指南「模特/场景图」tab 中文正文。2026-09-17 按业务最新版两份 docx 指南重写
 * （《象寄商品场景图生成指南-2026.9》《象寄商品模特图生成指南-2026.9》，同属模特/
 * 场景图产品，故合并为一个 tab、各成一个 h2 大节）；生成效果图两列并排（.v3-img-pair）。 */
const ZhContent: FC = () => {
  return (
    <>
      <h2>商品场景图生成</h2>

      <h3>一、商品场景图生成步骤</h3>
      <img alt="场景图生成入口" decoding="async" loading="lazy" src={sceneEntry} />
      <p>上传 1-3 张商品图片，完整多样的图片能生成效果更好、内容更准确的商品场景图。</p>
      <img alt="上传商品图示例" decoding="async" loading="lazy" src={sceneUploadSample} />
      <p>若有具体场景要求，点击：+输入文字描述（可选填），进行输入；若没有就不用填，交给 AI 就好了。</p>
      <p>输入描述时，请注意：</p>
      <ul>
        <li>
          要描述具体的场景，不要很概括只写“室内”。你希望商品在怎么样的一个场景里，可以写：商品在客厅的一个茶几上，前景有花朵，旁边有一个水杯。
        </li>
        <li>场景图会随机生成商品和人物的互动，如不需要人物，描述加上：画面里禁止出现任何人物。</li>
      </ul>
      <p>选择模型，尺寸比例、分辨率、张数，点击【立即生成】按钮就可以了。</p>

      <h3>二、再次生成</h3>
      <p>如需要更多场景图，在当前页，点击按钮【再次生成】即可；如果有指定场景要求，请点击【+输入文字描述】。</p>
      <img alt="再次生成" decoding="async" loading="lazy" src={sceneRegen} />
      <p>没有灵感，试试点击 AI 帮写，还可以对 AI 提示词进行修改，再生成。</p>
      <img alt="AI 帮写" decoding="async" loading="lazy" src={sceneAiWrite1} />
      <img alt="修改 AI 提示词再生成" decoding="async" loading="lazy" src={sceneAiWrite2} />

      <h3>三、生成效果展示</h3>
      <h4>树枝修剪刀</h4>
      <figure className="v3-img-pair">
        <img alt="树枝修剪刀场景图 1" decoding="async" loading="lazy" src={sceneShowPruner1} />
        <img alt="树枝修剪刀场景图 2" decoding="async" loading="lazy" src={sceneShowPruner2} />
      </figure>
      <h4>香水</h4>
      <figure className="v3-img-pair">
        <img alt="香水场景图 1" decoding="async" loading="lazy" src={sceneShowPerfume1} />
        <img alt="香水场景图 2" decoding="async" loading="lazy" src={sceneShowPerfume2} />
      </figure>
      <h4>耳机</h4>
      <figure className="v3-img-pair">
        <img alt="耳机场景图 1" decoding="async" loading="lazy" src={sceneShowEarphone1} />
        <img alt="耳机场景图 2" decoding="async" loading="lazy" src={sceneShowEarphone2} />
      </figure>
      <h4>晴雨伞</h4>
      <figure className="v3-img-pair">
        <img alt="晴雨伞场景图 1" decoding="async" loading="lazy" src={sceneShowUmbrella1} />
        <img alt="晴雨伞场景图 2" decoding="async" loading="lazy" src={sceneShowUmbrella2} />
      </figure>
      <h4>宠物喂食器</h4>
      <figure className="v3-img-pair">
        <img alt="宠物喂食器场景图 1" decoding="async" loading="lazy" src={sceneShowFeeder1} />
        <img alt="宠物喂食器场景图 2" decoding="async" loading="lazy" src={sceneShowFeeder2} />
      </figure>
      <h4>项链</h4>
      <figure className="v3-img-pair">
        <img alt="项链场景图 1" decoding="async" loading="lazy" src={sceneShowNecklace1} />
        <img alt="项链场景图 2" decoding="async" loading="lazy" src={sceneShowNecklace2} />
      </figure>
      <h4>精油</h4>
      <figure className="v3-img-pair">
        <img alt="精油场景图 1" decoding="async" loading="lazy" src={sceneShowOil1} />
        <img alt="精油场景图 2" decoding="async" loading="lazy" src={sceneShowOil2} />
      </figure>

      <h2>商品模特图生成</h2>

      <h3>一、商品模特图生成步骤</h3>
      <img alt="模特图生成入口" decoding="async" loading="lazy" src={modelEntry} />
      <p>
        上传 1-3 张商品图片，完整多样的图片能生成效果更好、内容更准确的模特展示图。最好上传产品的正面照、背面照，示例：
      </p>
      <img alt="上传正面照示例" decoding="async" loading="lazy" src={modelUploadFront} />
      <p>正面照</p>
      <img alt="AI 场景图示例" decoding="async" loading="lazy" src={modelUploadScene} />
      <p>AI 场景图</p>
      <p>若有具体模特要求，点击：+输入文字描述（可选填），进行输入；若没有就不用填，交给 AI 就好了。</p>
      <p>输入描述时，请输入你希望的模特：</p>
      <ul>
        <li>性别</li>
        <li>年龄或年龄范围（青年/中年/老年，或几岁）</li>
        <li>肤色（比如亚洲人/白人/黑人/拉丁裔等）</li>
        <li>其他（表情/发型等）</li>
      </ul>
      <p>如需要定制模特所在的场景，不要很概括只写“室内”，可以写：在美式复古的卧室里。</p>
      <p>描述举例：女，20 岁，白人，卷发，在美式复古卧室内。</p>
      <p>选择模型，尺寸比例、分辨率、张数，点击【立即生成】按钮就可以了。</p>

      <h3>二、再次生成</h3>
      <p>如需要更多模特图，在生成页，点击按钮【再次生成】即可；如需指定场景要求，请点击【+输入文字描述（可选填）】。</p>
      <img alt="再次生成" decoding="async" loading="lazy" src={modelRegen} />
      <p>没有灵感？试试点击 AI 帮写，还可以对 AI 提示词进行修改，再生成。</p>
      <img alt="AI 帮写" decoding="async" loading="lazy" src={modelAiWrite1} />
      <img alt="修改 AI 提示词再生成" decoding="async" loading="lazy" src={modelAiWrite2} />

      <h3>三、生成效果展示</h3>
      <h4>衣服</h4>
      <figure className="v3-img-pair">
        <img alt="衣服模特图 1" decoding="async" loading="lazy" src={modelShowClothes1} />
        <img alt="衣服模特图 2" decoding="async" loading="lazy" src={modelShowClothes2} />
      </figure>
      <h4>水杯</h4>
      <figure className="v3-img-pair">
        <img alt="水杯模特图 1" decoding="async" loading="lazy" src={modelShowCup1} />
        <img alt="水杯模特图 2" decoding="async" loading="lazy" src={modelShowCup2} />
      </figure>
      <h4>项链</h4>
      <figure className="v3-img-pair">
        <img alt="项链模特图 1" decoding="async" loading="lazy" src={modelShowNecklace1} />
        <img alt="项链模特图 2" decoding="async" loading="lazy" src={modelShowNecklace2} />
      </figure>
      <h4>台灯</h4>
      <figure className="v3-img-pair">
        <img alt="台灯模特图 1" decoding="async" loading="lazy" src={modelShowLamp1} />
        <img alt="台灯模特图 2" decoding="async" loading="lazy" src={modelShowLamp2} />
      </figure>
      <h4>树枝修剪刀</h4>
      <figure className="v3-img-pair">
        <img alt="树枝修剪刀模特图 1" decoding="async" loading="lazy" src={modelShowPruner1} />
        <img alt="树枝修剪刀模特图 2" decoding="async" loading="lazy" src={modelShowPruner2} />
      </figure>
      <h4>耳环</h4>
      <figure className="v3-img-pair">
        <img alt="耳环模特图 1" decoding="async" loading="lazy" src={modelShowEarring1} />
        <img alt="耳环模特图 2" decoding="async" loading="lazy" src={modelShowEarring2} />
      </figure>
    </>
  );
};

export default ZhContent;
